
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useParams, useLocation, Navigate } from "react-router-dom";
import { extractGameId } from "@/utils/gameSlug";
import { isDemoHost, DEMO_LEAGUES, getDemoLeagueBySlug, demoPublicPath } from "@/config/demo";
import { isEsportsHost, isEsportsDemoHost } from "@/config/esportsDomain";
import { isFirstPartyHost } from "@/config/customDomainHost";
import { esportsDemoLeaguePath, getEsportsDemoLeagueBySlug } from "@/config/esportsDemo";
import { ESPORTS_PROSPECT_DEMOS, getProspectDemoBySlug, prospectDemoHubPath } from "@/config/esportsProspectDemos";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminProvider } from "@/contexts/AdminContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import AdminImpersonationBanner from "@/components/AdminImpersonationBanner";
import CookieConsent from "@/components/CookieConsent";
import WidgetAutoResize from "@/components/widgets/WidgetAutoResize";
import ApexHostOnly from "@/components/ApexHostOnly";

import { Suspense, lazy } from "react";
import React from "react";
import { supabase } from "@/integrations/supabase/client";
import CustomizationInjector from "./components/CustomizationInjector";

// Core pages (loaded immediately). Index is the marketing home (prerendered,
// LCP-critical) and NotFound is the tiny catch-all fallback; the redirect stubs
// are trivial. Everything else is code-split below so the homepage bundle does
// not ship the dashboard, auth, or public-league code it never renders.
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import PublicLeagueRedirect from "./pages/PublicLeagueRedirect";

// Public team page
import PublicTeamRedirect from "./pages/PublicTeamRedirect";

// Auth, dashboard, and public-league pages: heavy and not needed on the
// marketing home, so load them on demand.
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PublicLeague = lazy(() => import("./pages/PublicLeague"));
const CustomDomainRoute = lazy(() => import("./pages/CustomDomainRoute"));
const DemoSwitcher = lazy(() => import("./pages/DemoSwitcher"));
const GameBoxScore = lazy(() => import("./pages/GameBoxScore"));

// Internal team page
const TeamPage = lazy(() => import("./pages/TeamPage"));

// Player pages
const PlayerPage = lazy(() => import("./pages/PlayerPage"));
const PublicPlayerPage = lazy(() => import("./pages/PublicPlayerPage"));

// Lazy load less critical pages for better performance
const Organization = lazy(() => import("./pages/Organization"));
const OrganizationSettings = lazy(() => import("./pages/OrganizationSettings"));
const OrganizationMembers = lazy(() => import("./pages/OrganizationMembers"));
const League = lazy(() => import("./pages/LeagueOverview"));
const ScoreEntry = lazy(() => import("./pages/ScoreEntry"));
const ManageLeague = lazy(() => import("./pages/ManageLeague"));
const LeagueMembers = lazy(() => import("./pages/LeagueMembers"));
const LeagueAuditLog = lazy(() => import("./pages/LeagueAuditLog"));
const PendingActions = lazy(() => import("./pages/PendingActions"));
const UserGuide = lazy(() => import("./pages/UserGuide"));
const Documentation = lazy(() => import("./pages/Documentation"));
const LeagueIntegration = lazy(() => import("./pages/LeagueIntegration"));
const LeagueCustomization = lazy(() => import("./pages/LeagueCustomization"));
const LeagueContent = lazy(() => import("./pages/LeagueContent"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PaddleCheckout = lazy(() => import("./pages/PaddleCheckout"));
const CheckoutProcessing = lazy(() => import("./pages/CheckoutProcessing"));
const Profile = lazy(() => import("./pages/Profile"));
const Features = lazy(() => import("./pages/Features"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AdminSports = lazy(() => import("./pages/AdminSports"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const Support = lazy(() => import("./pages/Support"));

// League sub-pages
const LeagueSchedule = lazy(() => import("./pages/LeagueSchedule"));
const LeagueVenues = lazy(() => import("./pages/LeagueVenues"));
const LeagueOfficials = lazy(() => import("./pages/LeagueOfficials"));
const LeagueDiscipline = lazy(() => import("./pages/LeagueDiscipline"));
const LeagueTeams = lazy(() => import("./pages/LeagueTeams"));
const LeagueStandings = lazy(() => import("./pages/LeagueStandings"));
const LeagueStats = lazy(() => import("./pages/LeagueStats"));
const LeagueSettings = lazy(() => import("./pages/LeagueSettings"));
const PlayoffManagement = lazy(() => import("./pages/PlayoffManagement"));

const OfficialScoring = lazy(() => import("./pages/OfficialScoring"));
const LiveScoringPage = lazy(() => import("./pages/LiveScoring"));
const ProductionReadinessCheck = lazy(() => import("./components/ProductionReadinessCheck"));

// Route guard for the authenticated organizer/dashboard screens. Lazy like
// the pages it wraps, mirroring EsportsAuthGuard below.
const DashboardAuthGuard = lazy(() => import("./components/DashboardAuthGuard"));

// Esports pages
// Lazy like the pages it wraps, so the guard and the esports chrome it renders
// stay out of the entry chunk for every sports visitor.
const EsportsAuthGuard = lazy(() => import("./components/esports/EsportsAuthGuard"));
const EsportsLanding = lazy(() => import("./pages/esports/EsportsLanding"));
const EsportsDashboard = lazy(() => import("./pages/esports/EsportsDashboard"));
const ManageEsportsLeague = lazy(() => import("./pages/esports/ManageEsportsLeague"));
const EsportsTeamsPage = lazy(() => import("./pages/esports/EsportsTeams"));
const EsportsSchedulePage = lazy(() => import("./pages/esports/EsportsSchedule"));
const EsportsSponsorsPage = lazy(() => import("./pages/esports/EsportsSponsors"));
const EsportsSponsorReportPage = lazy(() => import("./pages/esports/EsportsSponsorReport"));
const EsportsLeagueSettingsPage = lazy(() => import("./pages/esports/EsportsLeagueSettings"));
const EsportsSeasonsPage = lazy(() => import("./pages/esports/EsportsSeasons"));
const EsportsStageDetailPage = lazy(() => import("./pages/esports/EsportsStageDetail"));
const EsportsMatchEditorPage = lazy(() => import("./pages/esports/EsportsMatchEditor"));
const EsportsNewsPage = lazy(() => import("./pages/esports/EsportsNews"));
const EsportsDemoSwitcher = lazy(() => import("./pages/esports/EsportsDemoSwitcher"));
const EsportsPublicLeague = lazy(() => import("./pages/esports/EsportsPublicLeague"));
const EsportsMatchCentre = lazy(() => import("./pages/esports/EsportsMatchCentre"));
const EsportsTeamPage = lazy(() => import("./pages/esports/EsportsTeamPage"));
const EsportsPlayerPage = lazy(() => import("./pages/esports/EsportsPlayerPage"));
const EsportsArticlePage = lazy(() => import("./pages/esports/EsportsArticlePage"));

// Widget pages (public)
const StandingsWidgetPage = lazy(() => import("./pages/widgets/StandingsWidget"));
const SeasonSummaryWidgetPage = lazy(() => import("./pages/widgets/SeasonSummaryWidget"));
const GamesWidgetPage = lazy(() => import("./pages/widgets/GamesWidget"));
const PlayoffWidgetPage = lazy(() => import("./pages/widgets/PlayoffWidget"));
const AIHighlightsWidgetPage = lazy(() => import("./pages/widgets/AIHighlightsWidget"));
const HeadToHeadWidgetPage = lazy(() => import("./pages/widgets/HeadToHeadWidget"));
const LeagueLeadersWidgetPage = lazy(() => import("./pages/widgets/LeagueLeadersWidget"));
const PerformanceChartsWidgetPage = lazy(() => import("./pages/widgets/PerformanceChartsWidget"));
const TeamStatsWidgetPage = lazy(() => import("./pages/widgets/TeamStatsWidget"));
const TrendsWidgetPage = lazy(() => import("./pages/widgets/TrendsWidget"));
const PowerIndexWidgetPage = lazy(() => import("./pages/widgets/PowerIndexWidget"));
const ProgramsWidgetPage = lazy(() => import("./pages/widgets/ProgramsWidget"));
const OrgPrograms = lazy(() => import("./pages/OrgPrograms"));
const WidgetDocumentation = lazy(() => import("./pages/WidgetDocumentation"));

// Loading component for suspense
const Walkthrough = lazy(() => import("./pages/Walkthrough"));
const SportLanding = lazy(() => import("./pages/SportLanding"));
// Acquisition surfaces: the embed generator and its per-platform install
// guides, the free-migration landing pages, and vendor/procurement info for
// institutional buyers. All are prerendered except /embed, which needs live
// league data for its preview.
const EmbedGenerator = lazy(() => import("./pages/EmbedGenerator"));
const EmbedPlatformGuide = lazy(() => import("./pages/EmbedPlatformGuide"));
const LeagueImport = lazy(() => import("./pages/LeagueImport"));
const LeagueSponsors = lazy(() => import("./pages/LeagueSponsors"));
const LeagueSponsorReport = lazy(() => import("./pages/LeagueSponsorReport"));
const Migrate = lazy(() => import("./pages/Migrate"));
const MigrateQuickScores = lazy(() => import("./pages/MigrateQuickScores"));
const Vendor = lazy(() => import("./pages/Vendor"));

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// The sports demo hostname is a read-only showcase. Account and organizer
// routes preserve their path but move to the primary application hostname.
const PrimaryAppRoute = ({ children }: { children: React.ReactNode }) => (
  <ApexHostOnly>{children}</ApexHostOnly>
);

const OrganizerRoute = ({ children }: { children: React.ReactNode }) => (
  <ApexHostOnly>
    <DashboardAuthGuard>{children}</DashboardAuthGuard>
  </ApexHostOnly>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const WithLeagueCustomization = ({ children }: { children: React.ReactNode }) => {
  const params = useParams();
  const [leagueId, setLeagueId] = React.useState<string | null>(null);
  
  // Extract leagueId from various param names
  const directLeagueId = (params as any).id || (params as any).leagueId;
  // The box score route's :gameId segment may be a bare id or an SEO slug
  // ending in the id (e.g. lions-vs-tigers-<uuid>); extract the canonical
  // UUID the same way GameBoxScore.tsx does, otherwise the query below sends
  // the whole slug as the id and Postgres rejects it, so this wrapper's
  // leagueId (and the branding it drives) never resolves on a canonical URL.
  const gameId = extractGameId((params as any).gameId);
  
  // If we have a gameId (box score route), fetch the league ID from the game
  React.useEffect(() => {
    if (directLeagueId) {
      setLeagueId(directLeagueId);
    } else if (gameId) {
      const fetchLeagueId = async () => {
        const gameData = await queryClient.fetchQuery({
          queryKey: ['game-league', gameId],
          queryFn: async () => {
            const { data } = await supabase
              .from('games')
              .select('league_id')
              .eq('id', gameId)
              .single();
            return data;
          },
        });
        if (gameData?.league_id) {
          setLeagueId(gameData.league_id);
        }
      };
      fetchLeagueId();
    }
  }, [directLeagueId, gameId]);
  
  if (!leagueId) return <>{children}</>;
  return <CustomizationInjector leagueId={leagueId}>{children}</CustomizationInjector>;
};

// "/" is routed by hostname so one build serves both verticals PLUS every
// customer's own custom domain:
//   window.__STATHARBOUR_DOMAIN__ -> that league's public hub, no fetch
//   demo.esports.*                -> the esports title switcher (LoL / CS2)
//   esports.*                     -> the esports marketing landing
//   demo.*                        -> the multi-sport demo switcher
//   a non-first-party host        -> resolve and serve that league's hub
//   everything else               -> the sports marketing landing
// Order matters: demo.esports.* also starts with "demo." (so it satisfies
// isDemoHost), so the esports-demo check must come first. The custom-domain
// global is checked before all of it since api/public-league.js only ever
// injects it on a customer domain, never on statharbour.com or its
// subdomains, so it can never misfire ahead of the esports/demo checks.
const RootRoute = () => {
  const location = useLocation();
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const domainIds = window.__STATHARBOUR_DOMAIN__;
    if (domainIds?.leagueId && domainIds?.seasonId) {
      return (
        <CustomDomainHub
          leagueId={domainIds.leagueId}
          seasonId={domainIds.seasonId}
          search={location.search}
        />
      );
    }
    if (isEsportsDemoHost(host)) {
      // A switcher rather than a redirect to one title: a lead running a
      // Counter-Strike circuit who lands on a League of Legends page concludes
      // the product is for League of Legends. Deep links (/lol, /cs2) still go
      // straight to a single demo for a targeted email.
      return <EsportsDemoSwitcher />;
    }
    if (isEsportsHost(host)) {
      return <EsportsLanding />;
    }
    if (isDemoHost(host)) {
      return <DemoSwitcher />;
    }
    if (!isFirstPartyHost(host)) {
      // A client-side navigation back to "/", or a cached shell from before
      // this domain went active: window.__STATHARBOUR_DOMAIN__ was never
      // injected, so resolve it here instead.
      return <CustomDomainRoute />;
    }
  }
  return <Index />;
};

// PublicLeague reads its ids via useParams(), not props, so a custom domain
// (which carries no leagueId/seasonId in its URL at all) renders it inside a
// <Routes> given a synthetic `location`, the supported React Router pattern
// for matching a route element against a path other than the browser's own.
const CustomDomainHub = ({
  leagueId,
  seasonId,
  search = "",
}: {
  leagueId: string;
  seasonId: string;
  search?: string;
}) => (
  <Routes location={{ pathname: `/public/${leagueId}/${seasonId}`, search }}>
    <Route path="/public/:leagueId/:seasonId" element={<PublicLeague />} />
  </Routes>
);

// Per-sport demo deep link (/soccer, /baseball, ...). Redirects to that demo
// league's public hub, preserving the query string so the theming hook
// (?org=&primary=...) carries through to the public page.
const DemoDeepLink = ({ slug }: { slug: string }) => {
  const location = useLocation();
  const league = getDemoLeagueBySlug(slug);
  if (!league) return <NotFound />;
  return <Navigate to={`${demoPublicPath(league)}${location.search}`} replace />;
};

// Per-title esports demo deep link (/lol, /cs2). Redirects to that demo
// league's public hub, preserving the query string so any theming parameters
// carry through, exactly like DemoDeepLink does for the ball sports.
// A prospect demo's share link (demo.esports.statharbour.com/<slug>): straight
// to that league's public hub, keeping the query string. Registered per slug
// below rather than as a catch-all, so an unknown path still reaches NotFound.
const EsportsProspectDeepLink = ({ slug }: { slug: string }) => {
  const location = useLocation();
  const demo = getProspectDemoBySlug(slug);
  if (!demo) return <NotFound />;
  return <Navigate to={`${prospectDemoHubPath(demo)}${location.search}`} replace />;
};

const EsportsDemoDeepLink = ({ slug }: { slug: string }) => {
  const location = useLocation();
  const league = getEsportsDemoLeagueBySlug(slug);
  if (!league) return <NotFound />;
  return <Navigate to={`${esportsDemoLeaguePath(league)}${location.search}`} replace />;
};

// The full route table. Rendered inside whichever router (BrowserRouter on the
// client, StaticRouter during build-time prerender) wraps the providers below.
const AppRoutes = () => (
  <>
    <CookieConsent />
    {/* Reports document height to the parent window when a page is embedded in
        an iframe, so public/embed.js can size a widget frame to its content.
        Mounted here rather than in each widget page so every widget route,
        including ones added later, gets it. No-ops when not framed. */}
    <WidgetAutoResize />
    <Suspense fallback={<PageLoader />}>
      <Routes>
                    <Route path="/" element={<RootRoute />} />
                    <Route path="/features" element={<Features />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/forgot-password" element={<PrimaryAppRoute><ForgotPassword /></PrimaryAppRoute>} />
                    <Route path="/reset-password" element={<PrimaryAppRoute><ResetPassword /></PrimaryAppRoute>} />
                    <Route path="/login" element={<PrimaryAppRoute><Login /></PrimaryAppRoute>} />
                    <Route path="/signup" element={<PrimaryAppRoute><Signup /></PrimaryAppRoute>} />
                    <Route path="/invite/:token" element={<PrimaryAppRoute><AcceptInvitation /></PrimaryAppRoute>} />
                    <Route path="/dashboard" element={<OrganizerRoute><Dashboard /></OrganizerRoute>} />
                    <Route path="/profile" element={<OrganizerRoute><Profile /></OrganizerRoute>} />
                    <Route path="/pricing" element={<Pricing />} />
                    {/* Old URL from the pre-consolidation two-page pricing structure
                        (self-service vs. a sales-gated "competition pricing" page).
                        vercel.json also 301s it at the edge; this client-side route
                        covers a cached shell or an already-open tab. */}
                    <Route path="/grassroots-pricing" element={<Navigate to="/pricing" replace />} />
                    <Route path="/paddle-checkout" element={<OrganizerRoute><PaddleCheckout /></OrganizerRoute>} />
                    <Route path="/checkout-processing" element={<OrganizerRoute><CheckoutProcessing /></OrganizerRoute>} />
                    <Route path="/admin" element={<PrimaryAppRoute><AdminPanel /></PrimaryAppRoute>} />
                    <Route path="/admin/sports" element={<PrimaryAppRoute><AdminSports /></PrimaryAppRoute>} />
                    <Route path="/admin/support" element={<PrimaryAppRoute><AdminSupport /></PrimaryAppRoute>} />
                    <Route path="/support" element={<OrganizerRoute><Support /></OrganizerRoute>} />
                    <Route path="/production-check" element={<PrimaryAppRoute><div className="p-8 flex justify-center"><ProductionReadinessCheck /></div></PrimaryAppRoute>} />
                    <Route path="/organization/:id" element={<OrganizerRoute><Organization /></OrganizerRoute>} />
                    <Route path="/organization/:id/settings" element={<OrganizerRoute><OrganizationSettings /></OrganizerRoute>} />
                    <Route path="/organization/:id/members" element={<OrganizerRoute><OrganizationMembers /></OrganizerRoute>} />
                    <Route path="/league/:id" element={<OrganizerRoute><ManageLeague /></OrganizerRoute>} />
                    <Route path="/league/:id/manage" element={<OrganizerRoute><ManageLeague /></OrganizerRoute>} />
                    <Route path="/league/:id/score-entry" element={<OrganizerRoute><ScoreEntry /></OrganizerRoute>} />
                    <Route path="/league/:id/pending" element={<OrganizerRoute><PendingActions /></OrganizerRoute>} />
                    <Route path="/league/:id/integration" element={<OrganizerRoute><LeagueIntegration /></OrganizerRoute>} />
                    <Route path="/league/:id/customization" element={<OrganizerRoute><LeagueCustomization /></OrganizerRoute>} />
                    <Route path="/league/:id/content" element={<OrganizerRoute><LeagueContent /></OrganizerRoute>} />
                    <Route path="/league/:id/members" element={<OrganizerRoute><LeagueMembers /></OrganizerRoute>} />
                    <Route path="/league/:id/audit" element={<OrganizerRoute><LeagueAuditLog /></OrganizerRoute>} />
                    <Route path="/league/:id/schedule" element={<OrganizerRoute><LeagueSchedule /></OrganizerRoute>} />
                    <Route path="/league/:id/venues" element={<OrganizerRoute><LeagueVenues /></OrganizerRoute>} />
                    <Route path="/league/:id/officials" element={<OrganizerRoute><LeagueOfficials /></OrganizerRoute>} />
                    <Route path="/league/:id/discipline" element={<OrganizerRoute><LeagueDiscipline /></OrganizerRoute>} />
                    <Route path="/league/:id/teams" element={<OrganizerRoute><LeagueTeams /></OrganizerRoute>} />
                    <Route path="/league/:id/standings" element={<OrganizerRoute><LeagueStandings /></OrganizerRoute>} />
                    <Route path="/league/:id/stats" element={<OrganizerRoute><LeagueStats /></OrganizerRoute>} />
                    <Route path="/league/:id/settings" element={<OrganizerRoute><LeagueSettings /></OrganizerRoute>} />
                    <Route path="/league/:id/playoffs" element={<OrganizerRoute><PlayoffManagement /></OrganizerRoute>} />
                    <Route path="/league/:id/import" element={<OrganizerRoute><LeagueImport /></OrganizerRoute>} />
                    <Route path="/league/:id/sponsors" element={<OrganizerRoute><LeagueSponsors /></OrganizerRoute>} />
                    <Route path="/league/:id/sponsors/report" element={<OrganizerRoute><LeagueSponsorReport /></OrganizerRoute>} />
                    <Route path="/league/:leagueId/team/:teamId" element={<OrganizerRoute><TeamPage /></OrganizerRoute>} />
                    <Route path="/league/:leagueId/player/:playerId" element={<OrganizerRoute><PlayerPage /></OrganizerRoute>} />
                    <Route path="/user-guide" element={<UserGuide />} />
                    <Route path="/docs" element={<Documentation />} />
                    <Route path="/live-scoring/:leagueId" element={<OrganizerRoute><LiveScoringPage /></OrganizerRoute>} />
                    <Route path="/official-scoring" element={<PrimaryAppRoute><OfficialScoring /></PrimaryAppRoute>} />
                    {/* Backwards compatibility redirect for old public URLs without seasonId */}
                    <Route path="/public/:leagueId" element={<PublicLeagueRedirect />} />
                    {/* Season-specific public URL */}
                    <Route path="/public/:leagueId/:seasonId" element={<PublicLeague />} />
                    {/* Public team page (redirects to the in-page team view) */}
                    <Route path="/public/:leagueId/:seasonId/team/:teamId" element={<PublicTeamRedirect />} />
                    {/* Public player page */}
                    <Route path="/public/:leagueId/:seasonId/player/:playerId" element={<PublicPlayerPage />} />
                    <Route path="/game/:gameId/boxscore" element={<WithLeagueCustomization><GameBoxScore /></WithLeagueCustomization>} />
                    {/* Flat white-label routes are valid only on a resolved customer domain. */}
                    <Route path="/club/:teamId" element={<CustomDomainRoute />} />
                    <Route path="/player/:playerId" element={<CustomDomainRoute />} />
                    <Route path="/game/:gameId" element={<CustomDomainRoute />} />
                    {/* /demo and the demo subdomain root both show the
                        multi-sport demo switcher. Per-sport deep links
                        (/soccer, /baseball, ...) redirect to each demo
                        league's public hub, preserving theming query params. */}
                    <Route path="/demo" element={<DemoSwitcher />} />
                    {DEMO_LEAGUES.map((league) => (
                      <Route
                        key={league.slug}
                        path={`/${league.slug}`}
                        element={<DemoDeepLink slug={league.slug} />}
                      />
                    ))}
                    <Route path="/walkthrough" element={<Walkthrough />} />
                    <Route path="/sports/:sport" element={<SportLanding />} />
                    <Route path="/embed" element={<EmbedGenerator />} />
                    <Route path="/embed/:platform" element={<EmbedPlatformGuide />} />
                    <Route path="/migrate" element={<Migrate />} />
                    <Route path="/migrate/quickscores" element={<MigrateQuickScores />} />
                    <Route path="/vendor" element={<Vendor />} />
                    
                    {/* Widget routes (public, no auth required) */}
                    <Route path="/widget/standings/:leagueId" element={<WithLeagueCustomization><StandingsWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/games/:leagueId" element={<WithLeagueCustomization><GamesWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/season-summary/:leagueId" element={<WithLeagueCustomization><SeasonSummaryWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/season-summary/:leagueId/:seasonId" element={<WithLeagueCustomization><SeasonSummaryWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/playoffs/:leagueId" element={<WithLeagueCustomization><PlayoffWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/ai-highlights/:leagueId" element={<WithLeagueCustomization><AIHighlightsWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/head-to-head/:leagueId" element={<WithLeagueCustomization><HeadToHeadWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/league-leaders/:leagueId" element={<WithLeagueCustomization><LeagueLeadersWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/performance-charts/:leagueId" element={<WithLeagueCustomization><PerformanceChartsWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/team-stats/:leagueId" element={<WithLeagueCustomization><TeamStatsWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/trends/:leagueId" element={<WithLeagueCustomization><TrendsWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/power-index/:leagueId" element={<WithLeagueCustomization><PowerIndexWidgetPage /></WithLeagueCustomization>} />
                    <Route path="/widget/programs/:organizationId" element={<ProgramsWidgetPage />} />
                    <Route path="/programs/:organizationId" element={<OrgPrograms />} />
                    
                    {/* Widget Documentation */}
                    <Route path="/widget-docs" element={<WidgetDocumentation />} />
                    
                    {/* Esports routes */}
                    <Route path="/esports" element={<EsportsLanding />} />
                    <Route path="/esports/dashboard" element={<EsportsDashboard />} />
                    {/* Organizer screens sit behind the guard; the public hub below does not. */}
                    <Route path="/esports/leagues/:id" element={<EsportsAuthGuard><ManageEsportsLeague /></EsportsAuthGuard>} />
                    <Route path="/esports/leagues/:id/teams" element={<EsportsAuthGuard><EsportsTeamsPage /></EsportsAuthGuard>} />
                    <Route path="/esports/leagues/:id/schedule" element={<EsportsAuthGuard><EsportsSchedulePage /></EsportsAuthGuard>} />
                    <Route path="/esports/leagues/:id/sponsors" element={<EsportsAuthGuard><EsportsSponsorsPage /></EsportsAuthGuard>} />
                    <Route path="/esports/leagues/:id/sponsors/report" element={<EsportsAuthGuard><EsportsSponsorReportPage /></EsportsAuthGuard>} />
                    <Route path="/esports/leagues/:id/settings" element={<EsportsAuthGuard><EsportsLeagueSettingsPage /></EsportsAuthGuard>} />
                    <Route path="/esports/leagues/:id/seasons" element={<EsportsAuthGuard><EsportsSeasonsPage /></EsportsAuthGuard>} />
                    <Route path="/esports/leagues/:id/stages/:stageId" element={<EsportsAuthGuard><EsportsStageDetailPage /></EsportsAuthGuard>} />
                    <Route path="/esports/leagues/:id/matches/:matchId" element={<EsportsAuthGuard><EsportsMatchEditorPage /></EsportsAuthGuard>} />
                    <Route path="/esports/leagues/:id/news" element={<EsportsAuthGuard><EsportsNewsPage /></EsportsAuthGuard>} />
                    <Route path="/lol" element={<EsportsDemoDeepLink slug="lol" />} />
                    <Route path="/cs2" element={<EsportsDemoDeepLink slug="cs2" />} />
                    <Route path="/dota2" element={<EsportsDemoDeepLink slug="dota2" />} />
                    {ESPORTS_PROSPECT_DEMOS.map((d) => (
                      <Route key={d.slug} path={`/${d.slug}`} element={<EsportsProspectDeepLink slug={d.slug} />} />
                    ))}
                    <Route path="/esports/public/:leagueId" element={<EsportsPublicLeague />} />
                    <Route path="/esports/public/:leagueId/:seasonId" element={<EsportsPublicLeague />} />
                    {/* Detail pages are real routes rather than in-page state, so
                        Back works and a match link can be shared directly. */}
                    <Route path="/esports/public/:leagueId/:seasonId/match/:matchId" element={<EsportsMatchCentre />} />
                    <Route path="/esports/public/:leagueId/:seasonId/team/:teamId" element={<EsportsTeamPage />} />
                    <Route path="/esports/public/:leagueId/:seasonId/player/:playerId" element={<EsportsPlayerPage />} />
                    <Route path="/esports/public/:leagueId/:seasonId/news/:slug" element={<EsportsArticlePage />} />

        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </>
);

// All providers, with the router injected by the caller. The client passes a
// <BrowserRouter>; the build-time prerenderer (src/entry-server.tsx) passes a
// <StaticRouter>. Keeping the router swappable lets us render the marketing
// pages to static HTML for crawlers and AI assistants without changing client
// behavior. Exported for the SSR entry only.
export const AppProviders = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AdminProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AdminImpersonationBanner />
            {children}
          </TooltipProvider>
        </AdminProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

// Client entry: the SPA as it has always rendered, now expressed via the
// shared providers tree with a BrowserRouter.
const App = () => (
  <AppProviders>
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  </AppProviders>
);

export default App;
